import { createContext, useContext, useState } from "react";
import { useLocalStorage } from "../hooks/useLocalStorage";
import ICartItem from "../type/CartItem";

type ShoppingCartProviderProps = {
    children: React.ReactNode;
};

type ShoppingCartContextType = {
    cart: ICartItem[];
    openCart: () => void;
    closeCart: () => void;
    removeFromCart: (item: ICartItem) => void;
    addToCart: (item: ICartItem) => void;
    updateQuantity: (itemId: string, quantity: number) => void;
    cartQuantity: number;
    emptyCart: () => void;
};

export const ShoppingCartContext = createContext({} as ShoppingCartContextType);

export const useShoppingCart = () => {
    const context = useContext(ShoppingCartContext);
    if (!context) {
        throw new Error("useShoppingCart must be used within a ShoppingCartProvider");
    }
    return context;
};

export const ShoppingCartProvider = ({ children }: ShoppingCartProviderProps) => {
    const [cart, setCart] = useLocalStorage<ICartItem[]>(
        "cart",
        [] 
    );

    const [isCartOpen, setIsCartOpen] = useState(false);

    const cartQuantity = cart.reduce((total, item) => total + item.quantity, 0);

    const openCart = () => setIsCartOpen(true);
    const closeCart = () => setIsCartOpen(false);

    const addToCart = (item: ICartItem) => {
        setCart((prevCart) => [...prevCart, item]);
    };

    const removeFromCart = (item: ICartItem) => {
        setCart((prevCart) => prevCart.filter((i) => i.id !== item.id));
    };

    const updateQuantity = (itemId: string, quantity: number) => {
        if (quantity < 1) return;
        setCart((prevCart) =>
            prevCart.map((i) => (i.id === itemId ? { ...i, quantity } : i))
        );
    };

    const emptyCart = () => setCart([]);

    const contextValue: ShoppingCartContextType = {
        cart,
        removeFromCart,
        addToCart,
        updateQuantity,
        openCart,
        closeCart,
        cartQuantity,
        emptyCart
    };

    return (
        <ShoppingCartContext.Provider value={contextValue}>
            {children}
        </ShoppingCartContext.Provider>);
};